import { action, computed } from "mobx";
import { TKey } from "../../../../shared/translations/translationKeys";
import { getAverage } from "../../../core/utils";
import { ModuleDataType } from "../../core/BaseModuleDataModel";
import type BaseModuleModel from "../../core/BaseModuleModel";
import type { ModuleModelData, Snapshot } from "../../core/BaseModuleModel";
import LocationModel from "../../Location/LocationModel";
import RatingModel, { type RatingModelData } from "./RatingModel";

export class RatingManager<T extends Snapshot, T2 extends ModuleModelData<T>> {
  constructor(public module: BaseModuleModel<T, T2>) {}

  @action async rateDataItem(uid: string, rating: number) {
    const userId = this.module.plan.userInPlan?.uid;

    if (!userId) {
      return;
    }

    const item = this.getUserRating(uid);

    if (item) {
      item.setRating(rating);
    } else {
      this.module.addNewData<RatingModelData>(
        ModuleDataType.Rating,
        {
          userId: userId,
          rating,
          dataItemUid: uid,
        },
        TKey.ActivityRatingSet,
        {
          label: this.getLabelForDataUidRating(uid),
          rating,
        },
      );
    }
  }

  get(uid: string) {
    return this.ratings.filter((item) => item.data.dataItemUid === uid);
  }

  @computed private get ratings() {
    return (
      this.module.dataItems.filter((item): item is RatingModel => {
        return RatingModel.is(item);
      }) ?? []
    );
  }

  getLabelForDataUidRating(dataUid: string) {
    const dataItem = this.module.dataItems.find((d) => d.uid === dataUid);

    if (dataItem && LocationModel.is(dataItem)) {
      return dataItem.data.label;
    }

    return "-";
  }

  getRatings(dataUid: string, rating?: number) {
    return this.ratings.filter(
      (r) =>
        r.data.dataItemUid === dataUid &&
        (rating ? r.data.rating === rating : true),
    );
  }

  getUserRating(uid: string): RatingModel | undefined {
    return this.ratings.find((ratingItem) => {
      return (
        ratingItem.data.userId === this.module.userUid &&
        ratingItem.data.dataItemUid === uid
      );
    });
  }

  getUserHasRated(userUid: string): boolean {
    return this.ratings.some((ratingItem) => {
      return ratingItem.data.userId === userUid;
    });
  }

  getAverage(uid: string) {
    return getAverage(this.get(uid), (ratingItem) => ratingItem.data.rating);
  }
}
